import { useTranslation } from 'react-i18next';
import { Stack, Text } from '@mantine/core';
import { IconBuildingWarehouse, IconFileText, IconTruckLoading } from '@tabler/icons-react';
import LoadingConditionsSummary from '@/components/app/sell/LoadingConditionsSummary';
import OrderFileLinks from '@/components/common/OrderFileLinks';
import TermRow from '@/components/common/TermRow';
import type { LoadingConditions } from '@/types/sell';

// Минимальный набор условий продавца — одинаково у SaleOrderInfo (покупатель видит «Условия продавца»)
// и у верхнего уровня PurchaseOrderView (продавец видит свои «Ваши условия»).
interface SellerCoreTerms {
  readonly loadingConditions?: LoadingConditions;
  readonly declarationNo?: string;
  readonly declarationDate?: string;
  readonly declarationFiles?: readonly { readonly id: number; readonly name?: string }[];
}

interface SellerCoreTermsViewProps {
  readonly order: SellerCoreTerms;
  readonly onDownload: (id: number) => void;
  readonly downloadingId?: number;
}

// Ядро условий ПРОДАВЦА (read-only): условия погрузки → хранение → декларация. Единый вид для превью
// сделки: покупатель видит его как «Условия продавца», продавец — как свои «Ваши условия». Особые
// условия и прочие условия (комментарий) СЮДА не входят — это атрибут контрагента, показывается отдельно.
export default function SellerCoreTermsView({ order, onDownload, downloadingId }: SellerCoreTermsViewProps) {
  const { t } = useTranslation();
  const lc = order.loadingConditions;
  return (
    <Stack gap="md">
      {lc ? (
        <TermRow icon={<IconTruckLoading size={18} />} label={t('app.sell.loading.title')}>
          <LoadingConditionsSummary lc={lc} />
        </TermRow>
      ) : null}

      {lc ? (
        <TermRow icon={<IconBuildingWarehouse size={18} />} label={t('app.sell.terms.storage')}>
          <Text size="sm" c="gray.8">
            {lc.storageAvailable
              ? t('app.sell.terms.storageAvailable', { price: lc.storagePrice ?? 0 })
              : t('app.sell.terms.storageUrgent')}
          </Text>
        </TermRow>
      ) : null}

      {order.declarationNo ? (
        <TermRow icon={<IconFileText size={18} />} label={t('app.sell.terms.declaration')}>
          <Stack gap={4}>
            <Text size="sm" c="gray.8">
              № {order.declarationNo}
              {order.declarationDate ? ` · ${order.declarationDate}` : ''}
            </Text>
            <OrderFileLinks files={order.declarationFiles ?? []} onDownload={onDownload} downloadingId={downloadingId} />
          </Stack>
        </TermRow>
      ) : null}
    </Stack>
  );
}
